// EX-QC-01: drawer rápido = identidad + periodo + 4 KPIs + cobertura.
// EX-QC-02: variación direccional + actividad compacta.
// EX-QC-05: posición interna + mix de clientes.

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import {
  Alert,
  Box,
  Button,
  Drawer,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import {
  getMarginCenterProductContext,
  getMarginCenterProductCustomers,
  getMarginCenterProductPosition,
} from '../services/marginCenterService';

import type {
  MarginCenterProductContextParams,
  MarginCenterProductCustomersParams,
} from '../types/marginCenter';

import type {
  MarginCenterProductPositionParams,
} from '../types/productPosition';

const numberFormatter = new Intl.NumberFormat('es-ES', {
  maximumFractionDigits: 2,
});

const integerFormatter = new Intl.NumberFormat('es-ES', {
  maximumFractionDigits: 0,
});

function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return numberFormatter.format(value);
}

function formatInteger(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return integerFormatter.format(value);
}

function formatPct(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return `${numberFormatter.format(value)} %`;
}

function formatSignedPct(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return `${value > 0 ? '+' : ''}${numberFormatter.format(value)} %`;
}

function formatSignedPp(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return `${value > 0 ? '+' : ''}${numberFormatter.format(value)} pp`;
}

function formatSignedInteger(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return `${value > 0 ? '+' : ''}${integerFormatter.format(value)}`;
}

function changeColor(
  value: number | null | undefined,
): string {
  if (value === null || value === undefined || value === 0) {
    return '#94a3b8';
  }

  return value > 0 ? '#34d399' : '#fb7185';
}

function formatCurrency(
  value: number | null | undefined,
  currencyCode: string | null,
): string {
  if (value === null || value === undefined) return '—';
  if (!currencyCode) return formatNumber(value);

  try {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: currencyCode,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return formatNumber(value);
  }
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return value;

  return new Intl.DateTimeFormat('es-ES', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(new Date(year, month - 1, day));
}

function comparisonLabel(value: string): string {
  switch (value) {
    case 'none':
      return 'Sin comparación';
    case 'previous_period':
      return 'vs periodo anterior';
    case 'year_over_year':
      return 'vs mismo periodo año anterior';
    case 'custom':
      return 'vs comparación personalizada';
    default:
      return 'Comparación';
  }
}

function grainLabel(value: string): string {
  switch (value) {
    case 'day':
      return 'Día';
    case 'month':
      return 'Mes';
    default:
      return 'Semana';
  }
}

function QuickMetric({
  label,
  value,
  comparison,
  change,
  changeValue,
}: {
  label: string;
  value: string;
  comparison?: string;
  change?: string;
  changeValue?: number | null;
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.35,
        minHeight: 88,
        borderRadius: 2.25,
        border: '1px solid rgba(148,163,184,0.14)',
        bgcolor: 'rgba(15,23,42,0.50)',
      }}
    >
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          color: '#94a3b8',
          fontWeight: 750,
          lineHeight: 1.25,
        }}
      >
        {label}
      </Typography>

      <Typography
        variant="h6"
        sx={{
          mt: 0.35,
          color: '#f8fafc',
          fontWeight: 900,
          lineHeight: 1.2,
        }}
      >
        {value}
      </Typography>

      {(comparison || change) && (
        <Stack
          direction="row"
          spacing={0.55}
          useFlexGap
          flexWrap="wrap"
          alignItems="baseline"
          sx={{ mt: 0.4 }}
        >
          {comparison && (
            <Typography
              variant="caption"
              sx={{
                color: '#64748b',
                lineHeight: 1.3,
              }}
            >
              {comparison}
            </Typography>
          )}

          {change && (
            <Typography
              variant="caption"
              sx={{
                color: changeColor(changeValue),
                fontWeight: 850,
                lineHeight: 1.3,
              }}
            >
              {change}
            </Typography>
          )}
        </Stack>
      )}
    </Paper>
  );
}

function formatRank(
  rank: number | null | undefined,
  population: number | null | undefined,
): string {
  if (
    rank === null
    || rank === undefined
    || population === null
    || population === undefined
  ) {
    return '—';
  }

  return `#${formatInteger(rank)} de ${formatInteger(population)}`;
}

function rankMovement(
  value: number | null | undefined,
): {
  text: string;
  color: string;
} {
  if (value === null || value === undefined) {
    return {
      text: 'Sin comparación',
      color: '#64748b',
    };
  }

  if (value > 0) {
    return {
      text: `↑ ${formatInteger(value)} puestos`,
      color: '#34d399',
    };
  }

  if (value < 0) {
    return {
      text: `↓ ${formatInteger(Math.abs(value))} puestos`,
      color: '#fb7185',
    };
  }

  return {
    text: '= sin cambio',
    color: '#94a3b8',
  };
}

function InsightRow({
  label,
  value,
  detail,
  detailColor = '#64748b',
}: {
  label: string;
  value: string;
  detail?: string;
  detailColor?: string;
}) {
  return (
    <Box
      sx={{
        minWidth: 0,
        py: 0.55,
        borderBottom:
          '1px solid rgba(148,163,184,0.08)',
        '&:last-of-type': {
          borderBottom: 'none',
        },
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="baseline"
        justifyContent="space-between"
      >
        <Typography
          variant="caption"
          sx={{
            color: '#94a3b8',
            fontWeight: 750,
            lineHeight: 1.25,
            minWidth: 0,
          }}
        >
          {label}
        </Typography>

        <Typography
          variant="body2"
          noWrap
          sx={{
            color: '#f8fafc',
            fontWeight: 900,
            lineHeight: 1.25,
            maxWidth: '62%',
            textAlign: 'right',
          }}
        >
          {value}
        </Typography>
      </Stack>

      {detail && (
        <Typography
          variant="caption"
          noWrap
          sx={{
            display: 'block',
            mt: 0.15,
            color: detailColor,
            lineHeight: 1.25,
            textAlign: 'right',
          }}
        >
          {detail}
        </Typography>
      )}
    </Box>
  );
}

export default function ProductQuickContextDrawer({
  productId,
  contextParams,
  currencyCode,
  onClose,
  onOpenFull,
}: {
  productId: string | null;
  contextParams: MarginCenterProductContextParams;
  currencyCode: string | null;
  onClose: () => void;
  onOpenFull: () => void;
}) {
  const contextQuery = useQuery({
    queryKey: ['margin-center', 'product-context', productId, contextParams],
    queryFn: () =>
      getMarginCenterProductContext(productId ?? '', contextParams),
    enabled: Boolean(productId),
  });

  const positionParams =
    useMemo<MarginCenterProductPositionParams>(
      () => ({
        start_date: contextParams.start_date,
        end_date: contextParams.end_date,
        comparison_mode: contextParams.comparison_mode,
        comparison_start_date:
          contextParams.comparison_start_date,
        comparison_end_date:
          contextParams.comparison_end_date,
      }),
      [
        contextParams.comparison_end_date,
        contextParams.comparison_mode,
        contextParams.comparison_start_date,
        contextParams.end_date,
        contextParams.start_date,
      ],
    );

  const customersParams =
    useMemo<MarginCenterProductCustomersParams>(
      () => ({
        start_date: contextParams.start_date,
        end_date: contextParams.end_date,
        comparison_mode: contextParams.comparison_mode,
        comparison_start_date:
          contextParams.comparison_start_date,
        comparison_end_date:
          contextParams.comparison_end_date,
        sort_field: 'revenue',
        sort_direction: 'desc',
        limit: 1,
      }),
      [
        contextParams.comparison_end_date,
        contextParams.comparison_mode,
        contextParams.comparison_start_date,
        contextParams.end_date,
        contextParams.start_date,
      ],
    );

  const positionQuery = useQuery({
    queryKey: [
      'margin-center',
      'product-position',
      'quick-context',
      productId,
      positionParams,
    ],
    queryFn: () =>
      getMarginCenterProductPosition(
        productId ?? '',
        positionParams,
      ),
    enabled: Boolean(productId),
    staleTime: 20_000,
    retry: false,
  });

  const customersQuery = useQuery({
    queryKey: [
      'margin-center',
      'product-customers',
      'quick-context',
      productId,
      customersParams,
    ],
    queryFn: () =>
      getMarginCenterProductCustomers(
        productId ?? '',
        customersParams,
      ),
    enabled: Boolean(productId),
    staleTime: 20_000,
  });

  const context = contextQuery.data;
  const current = context?.current ?? null;
  const comparison = context?.comparison ?? null;
  const changes = context?.changes ?? null;
  const hasComparison =
    context?.selection.comparison_mode !== 'none';

  const coverage = context?.coverage?.current ?? null;
  const showCoverage = Boolean(
    coverage
    && (
      coverage.cost_coverage_pct < 100
      || coverage.resolved_revenue_coverage_pct < 100
    ),
  );

  const position =
    positionQuery.data?.status === 'ok'
      ? positionQuery.data
      : null;

  const globalPosition =
    position?.global ?? null;

  const groupPosition =
    position?.group ?? null;

  const globalRevenueMovement =
    rankMovement(
      globalPosition?.revenue.rank_change,
    );

  const groupRevenueMovement =
    rankMovement(
      groupPosition?.revenue.rank_change,
    );

  const globalMargin =
    globalPosition?.margin_percentage ?? null;

  const marginVsMedian =
    globalMargin?.current_value !== null
    && globalMargin?.current_value !== undefined
    && globalMargin?.current_median !== null
    && globalMargin?.current_median !== undefined
      ? (
        globalMargin.current_value
        - globalMargin.current_median
      )
      : null;

  const customerPortfolio =
    customersQuery.data?.status === 'ok'
      ? customersQuery.data
      : null;

  const portfolioAnalysis =
    customerPortfolio?.portfolio_analysis ?? null;

  const mix =
    portfolioAnalysis?.mix_movement ?? null;

  const topShareGain =
    mix?.status === 'ok'
      ? mix.largest_share_gains[0] ?? null
      : null;

  const topShareLoss =
    mix?.status === 'ok'
      ? mix.largest_share_losses[0] ?? null
      : null;

  const top5Share =
    customerPortfolio
      ?.portfolio_summary
      .current_top_5_revenue_share_pct
    ?? null;

  return (
    <Drawer
      anchor="right"
      open={Boolean(productId)}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 520, md: 560 },
          bgcolor: '#0b1220',
          borderLeft: '1px solid rgba(148,163,184,0.18)',
          overflowY: 'hidden',
        },
      }}
    >
      <Box
        sx={{
          p: 2.5,
          height: '100%',
          boxSizing: 'border-box',
        }}
      >
        <Stack spacing={1.75}>
          <Stack
            direction="row"
            spacing={2}
            alignItems="flex-start"
            justifyContent="space-between"
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="overline"
                sx={{
                  color: '#38bdf8',
                  fontWeight: 800,
                  letterSpacing: 1,
                  lineHeight: 1.2,
                }}
              >
                Contexto comercial · Producto
              </Typography>

              <Typography
                variant="h5"
                sx={{
                  mt: 0.25,
                  color: '#f8fafc',
                  fontWeight: 900,
                  lineHeight: 1.2,
                }}
              >
                {context?.product?.label ?? productId ?? 'Producto'}
              </Typography>

              {productId && (
                <Typography
                  variant="caption"
                  sx={{
                    mt: 0.35,
                    display: 'block',
                    color: '#64748b',
                  }}
                >
                  ID {productId}
                </Typography>
              )}
            </Box>

            <Button
              size="small"
              onClick={onClose}
              sx={{ textTransform: 'none' }}
            >
              Cerrar
            </Button>
          </Stack>

          {context?.selection && (
            <Typography
              variant="caption"
              sx={{ color: '#94a3b8', lineHeight: 1.4 }}
            >
              {formatDate(context.selection.start_date)}
              {' — '}
              {formatDate(context.selection.end_date)}
              {' · '}
              {comparisonLabel(context.selection.comparison_mode)}
              {' · '}
              {grainLabel(context.selection.time_grain)}
            </Typography>
          )}

          {contextQuery.isLoading && (
            <Stack spacing={1}>
              <LinearProgress />
              <Typography
                variant="body2"
                sx={{ color: '#94a3b8' }}
              >
                Cargando contexto del producto…
              </Typography>
            </Stack>
          )}

          {contextQuery.isError && (
            <Alert
              severity="error"
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => contextQuery.refetch()}
                >
                  Reintentar
                </Button>
              }
            >
              No se pudo consultar el contexto del producto.
            </Alert>
          )}

          {context?.status === 'product_not_found' && (
            <Alert severity="warning">
              El producto no existe en la cuenta actual.
            </Alert>
          )}

          {context?.status === 'empty_dataset' && (
            <Alert severity="info">
              Esta cuenta todavía no dispone de actividad transaccional.
            </Alert>
          )}

          {context?.status === 'no_results' && (
            <Alert severity="info">
              El producto existe, pero no tiene actividad en los periodos seleccionados.
            </Alert>
          )}

          {context?.status === 'ok' && current && (
            <>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                  gap: 1,
                }}
              >
                <QuickMetric
                  label="Ingresos"
                  value={formatCurrency(current.revenue, currencyCode)}
                  comparison={
                    hasComparison && comparison
                      ? `${formatCurrency(
                          comparison.revenue,
                          currencyCode,
                        )} ant.`
                      : undefined
                  }
                  change={
                    hasComparison && comparison
                      ? formatSignedPct(
                          changes?.revenue_delta_pct,
                        )
                      : undefined
                  }
                  changeValue={changes?.revenue_delta_pct}
                />

                <QuickMetric
                  label="Margen %"
                  value={formatPct(current.margin_percentage)}
                  comparison={
                    hasComparison && comparison
                      ? `${formatPct(
                          comparison.margin_percentage,
                        )} ant.`
                      : undefined
                  }
                  change={
                    hasComparison && comparison
                      ? formatSignedPp(
                          changes?.margin_percentage_delta_points,
                        )
                      : undefined
                  }
                  changeValue={
                    changes?.margin_percentage_delta_points
                  }
                />

                <QuickMetric
                  label="Clientes activos"
                  value={formatInteger(current.active_customers)}
                  comparison={
                    hasComparison && comparison
                      ? `${formatInteger(
                          comparison.active_customers,
                        )} ant.`
                      : undefined
                  }
                  change={
                    hasComparison && comparison
                      ? formatSignedInteger(
                          changes?.active_customers_delta,
                        )
                      : undefined
                  }
                  changeValue={changes?.active_customers_delta}
                />

                <QuickMetric
                  label="Unidades"
                  value={formatNumber(current.units)}
                  comparison={
                    hasComparison && comparison
                      ? `${formatNumber(
                          comparison.units,
                        )} ant.`
                      : undefined
                  }
                  change={
                    hasComparison && comparison
                      ? formatSignedPct(
                          changes?.units_delta_pct,
                        )
                      : undefined
                  }
                  changeValue={changes?.units_delta_pct}
                />
              </Box>

              <Paper
                elevation={0}
                sx={{
                  px: 1.35,
                  py: 1.05,
                  borderRadius: 2.25,
                  border:
                    '1px solid rgba(56,189,248,0.16)',
                  bgcolor: 'rgba(8,47,73,0.12)',
                  minWidth: 0,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    color: '#38bdf8',
                    fontWeight: 850,
                    mb: 0.35,
                  }}
                >
                  Posición interna
                </Typography>

                <InsightRow
                  label="Cuenta · ingresos"
                  value={formatRank(
                    globalPosition?.revenue.current_rank,
                    globalPosition?.revenue.current_population,
                  )}
                  detail={
                    globalPosition
                      ? `${
                          formatPct(
                            globalPosition
                              .revenue
                              .current_share_pct,
                          )
                        } de ingresos · ${
                          globalRevenueMovement.text
                        }`
                      : positionQuery.isLoading
                        ? 'Calculando posición…'
                        : 'No disponible'
                  }
                  detailColor={
                    globalRevenueMovement.color
                  }
                />

                <InsightRow
                  label={
                    position?.selected_group
                      ? `${
                          position.selected_group.label
                        } · ${
                          position.selected_group.value
                        }`
                      : 'Categoría / marca'
                  }
                  value={formatRank(
                    groupPosition?.revenue.current_rank,
                    groupPosition?.revenue.current_population,
                  )}
                  detail={
                    groupPosition
                      ? groupRevenueMovement.text
                      : 'No disponible para este producto'
                  }
                  detailColor={
                    groupRevenueMovement.color
                  }
                />

                <InsightRow
                  label="Margen %"
                  value={
                    globalMargin?.current_percentile !== null
                    && globalMargin?.current_percentile !== undefined
                      ? `Percentil ${
                          formatNumber(
                            globalMargin.current_percentile,
                          )
                        }`
                      : '—'
                  }
                  detail={
                    globalMargin?.current_median !== null
                    && globalMargin?.current_median !== undefined
                      ? `Mediana cuenta ${
                          formatPct(
                            globalMargin.current_median,
                          )
                        }`
                      : 'Sin mediana calculable'
                  }
                  detailColor={
                    changeColor(marginVsMedian)
                  }
                />
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  px: 1.35,
                  py: 1.05,
                  borderRadius: 2.25,
                  border:
                    '1px solid rgba(148,163,184,0.12)',
                  bgcolor: 'rgba(2,6,23,0.22)',
                  minWidth: 0,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    color: '#94a3b8',
                    fontWeight: 850,
                    mb: 0.35,
                  }}
                >
                  Mix de clientes
                </Typography>

                <InsightRow
                  label="Gana peso"
                  value={
                    topShareGain?.label
                    ?? (
                      customersQuery.isLoading
                        ? 'Analizando…'
                        : '—'
                    )
                  }
                  detail={
                    topShareGain
                      ? `${
                          formatSignedPp(
                            topShareGain
                              .revenue_share_delta_points,
                          )
                        } · ${
                          formatPct(
                            topShareGain
                              .current_revenue_share_pct,
                          )
                        } actual`
                      : (
                        hasComparison
                          ? 'Sin ganancia de peso destacada'
                          : 'Requiere comparación'
                      )
                  }
                  detailColor={
                    changeColor(
                      topShareGain
                        ?.revenue_share_delta_points,
                    )
                  }
                />

                <InsightRow
                  label="Pierde peso"
                  value={topShareLoss?.label ?? '—'}
                  detail={
                    topShareLoss
                      ? `${
                          formatSignedPp(
                            topShareLoss
                              .revenue_share_delta_points,
                          )
                        } · ${
                          formatPct(
                            topShareLoss
                              .current_revenue_share_pct,
                          )
                        } actual`
                      : (
                        hasComparison
                          ? 'Sin pérdida de peso destacada'
                          : 'Requiere comparación'
                      )
                  }
                  detailColor={
                    changeColor(
                      topShareLoss
                        ?.revenue_share_delta_points,
                    )
                  }
                />

                <InsightRow
                  label="Concentración"
                  value={
                    top5Share !== null
                      ? `Top 5 · ${formatPct(top5Share)}`
                      : '—'
                  }
                  detail={
                    mix?.status === 'ok'
                      ? `${
                          formatInteger(
                            mix.gaining_share_customers,
                          )
                        } ganan · ${
                          formatInteger(
                            mix.losing_share_customers,
                          )
                        } pierden peso`
                      : 'Peso sobre ingresos actuales'
                  }
                />
              </Paper>

              {showCoverage && coverage && (
                <Typography
                  variant="caption"
                  sx={{ color: '#94a3b8', lineHeight: 1.4 }}
                >
                  Cobertura de coste: {formatPct(coverage.cost_coverage_pct)}
                  {' de filas · '}
                  {formatPct(coverage.resolved_revenue_coverage_pct)}
                  {' de ingresos'}
                </Typography>
              )}

              <Button
                variant="contained"
                onClick={onOpenFull}
                fullWidth
                sx={{
                  mt: 0.25,
                  textTransform: 'none',
                  fontWeight: 850,
                }}
              >
                Ver análisis completo
              </Button>
            </>
          )}
        </Stack>
      </Box>
    </Drawer>
  );
}
